import { useEvaluateReferenceFilter } from './useEvaluateReferenceFilter'
import { useColumns } from 'app/hooks/useColumns';
import { useTables } from 'app/hooks/useTables';
import { RecordManagement } from 'app/class/record-management';
import SysColumn from 'app/class/sys-column';
import SysTable from 'app/class/sys-table';
import { useQueries } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { useContext, useMemo } from 'react';
import { useReferenceOptions } from 'modules/record/shared/hooks/useReferenceOptions';
import { RecordProducerContext } from '../components/RecordProducerContext';
import { buildReferenceFilterContext, collectReferenceFilterEntries, ReferenceFilterDependencyDescriptor } from './referenceFilterContext';
import queryKeys from "shared/services/queryKeys";

const extractReferenceFilterPaths = (referenceFilter: string): string[] => {
    if (!referenceFilter) {
        return [];
    }

    const matches = referenceFilter.matchAll(/\$\{([^}]+)\}/g);
    return Array.from(
        new Set(Array.from(matches, ([, path]) => path.trim()).filter(Boolean))
    );
};

const getReferenceId = (value: any): string => {
    if (typeof value === "string") {
        return value;
    }

    if (value && typeof value === "object" && "id" in value) {
        return String(value.id ?? "");
    }

    return "";
};

const getRecordFieldValue = (record: any, fieldName: string) => {
    if (!record) {
        return undefined;
    }

    if (record[fieldName] !== undefined) {
        return record[fieldName];
    }

    if (record.data && typeof record.data === "object") {
        return record.data[fieldName];
    }

    return undefined;
};

const columnsForTable = (columns: SysColumn[], tableId?: string) =>
    columns.filter((column) => column.sys_table_id === tableId && column.active !== false);

const tableByName = (tables: SysTable[], tableName?: string) =>
    tables.find((table) => table.name === tableName);

const columnByName = (columns: SysColumn[], name?: string) =>
    columns.find((column) => column.name === name);

const pathWithoutCurrentTable = (path: string, currentTableName?: string) => {
    const segments = path.split(".").filter(Boolean);

    if (segments.length > 1 && segments[0] === currentTableName) {
        return segments.slice(1);
    }

    return segments;
};

const resolveWatchedFieldPath = (path: string, currentTableName?: string) => {
    const segments = pathWithoutCurrentTable(path, currentTableName);
    return segments[0] || path;
};

const resolveReferenceDotWalkValue = async ({
    rootValue,
    fieldPathSegments,
    currentTable,
    allColumns,
    tables,
}: {
    rootValue: any;
    fieldPathSegments: string[];
    currentTable?: SysTable;
    allColumns: SysColumn[];
    tables: SysTable[];
}) => {
    if (fieldPathSegments.length <= 1) {
        return rootValue;
    }

    let currentValue = getReferenceId(rootValue);
    let currentColumns = columnsForTable(allColumns, currentTable?.id);

    for (let index = 0; index < fieldPathSegments.length - 1; index += 1) {
        const segment = fieldPathSegments[index];
        const nextSegment = fieldPathSegments[index + 1];
        const column = columnByName(currentColumns, segment);

        if (!column?.referenced_table_name || !currentValue) {
            return undefined;
        }

        const referencedTable = tableByName(tables, column.referenced_table_name);
        if (!referencedTable?.name) {
            return undefined;
        }

        const record = await new RecordManagement(referencedTable.name).get(currentValue);
        const nextValue = getRecordFieldValue(record, nextSegment);

        if (index === fieldPathSegments.length - 2) {
            return nextValue;
        }

        currentValue = getReferenceId(nextValue);
        currentColumns = columnsForTable(allColumns, referencedTable.id);
    }

    return undefined;
};

export const useReferenceSearch = (
    tableName: string,
    referenceFilter: string,
    selectedId: string,
    search: string,
    displayColumnName: string,
    loadOptions = true
) => {
    const { evaluateReferenceFilter } = useEvaluateReferenceFilter();
    const { control } = useFormContext();
    const recordProducerContext = useContext(RecordProducerContext);
    const { data: allColumns = [] } = useColumns();
    const { data: tables = [] } = useTables();
    const currentTableName = recordProducerContext?.table?.name;
    const dependencyPaths = useMemo(
        () => extractReferenceFilterPaths(referenceFilter),
        [referenceFilter]
    );
    const dependencyDescriptors = useMemo(
        (): ReferenceFilterDependencyDescriptor[] => dependencyPaths.map((path) => {
            const fieldPathSegments = pathWithoutCurrentTable(path, currentTableName);

            return {
                sourcePath: path,
                watchPath: resolveWatchedFieldPath(path, currentTableName),
                fieldPathSegments,
                needsDotWalkResolution: fieldPathSegments.length > 1,
            };
        }),
        [dependencyPaths, currentTableName]
    );
    const watchedDependencyValues = useWatch({
        control,
        name: dependencyDescriptors.map((descriptor) => descriptor.watchPath),
    });
    const dotWalkQueryResults = useQueries({
        queries: dependencyDescriptors.map((descriptor, index) => {
            const rootValue = Array.isArray(watchedDependencyValues)
                ? watchedDependencyValues[index]
                : watchedDependencyValues;

            return {
                queryKey: queryKeys.records.lookup(
                    currentTableName || "",
                    "dot-walk",
                    descriptor.sourcePath,
                    getReferenceId(rootValue) || String(rootValue ?? "")
                ),
                enabled: Boolean(
                    descriptor.needsDotWalkResolution &&
                    currentTableName &&
                    getReferenceId(rootValue) &&
                    allColumns.length &&
                    tables.length
                ),
                queryFn: () => resolveReferenceDotWalkValue({
                    rootValue,
                    fieldPathSegments: descriptor.fieldPathSegments,
                    currentTable: recordProducerContext?.table,
                    allColumns,
                    tables,
                }),
                staleTime: 60 * 1000,
                retry: false,
            };
        }),
    });
    const dependencyValues = useMemo(() => {
        const entries = collectReferenceFilterEntries(
            dependencyDescriptors, watchedDependencyValues, dotWalkQueryResults
        );
        return buildReferenceFilterContext(entries);
    }, [dependencyDescriptors, dotWalkQueryResults, watchedDependencyValues]);
    const isResolvingDotWalkDependencies = dependencyDescriptors.some((descriptor, index) => {
        if (!descriptor.needsDotWalkResolution) {
            return false;
        }

        const value = Array.isArray(watchedDependencyValues)
            ? watchedDependencyValues[index]
            : watchedDependencyValues;

        return Boolean(getReferenceId(value) && dotWalkQueryResults[index]?.isLoading);
    });

    const evaluatedFilters = useMemo(() => {
        if (!referenceFilter || isResolvingDotWalkDependencies) {
            return null;
        }

        return evaluateReferenceFilter(referenceFilter, dependencyValues);
    }, [dependencyValues, evaluateReferenceFilter, isResolvingDotWalkDependencies, referenceFilter]);

    const options = useReferenceOptions({
        tableName: isResolvingDotWalkDependencies ? "" : tableName,
        selectedId,
        search,
        displayColumnName,
        evaluatedFilters,
        loadOptions,
    });
    const tableChainTargetId = (evaluatedFilters as any)?.conditions?.find(
        (condition: any) => condition.field === "sys_table_id" && String(condition.operator).startsWith("in_table_chain")
    )?.value;
    return { ...options, tableChainTargetId: String(tableChainTargetId || "") };
}
